import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Eye, FileDown, Users, Search, ClipboardList, Bell, CalendarDays, FileSignature } from "lucide-react";

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
}

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/forms");
        setForms(res.data);
      } catch (e) {
        toast.error("Could not load submissions.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadPdf = async (userId, formId, name) => {
    try {
      const res = await api.get(`/admin/forms/${userId}/pdf`, { responseType: "blob", params: formId ? { form_id: formId } : {} });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `paperwork-${(name || "patient").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF.");
    }
  };

  const viewSigned = async (userId, formId) => {
    try {
      const res = await api.get(`/admin/forms/${userId}/signed-pdf`, { responseType: "blob", params: formId ? { form_id: formId } : {} });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open the signed document.");
    }
  };

  const setAppointment = async (userId, formId, date) => {
    setForms((prev) => prev.map((f) => (f.form_id === formId ? { ...f, appointment_date: date } : f)));
    try {
      await api.put(`/admin/forms/${userId}/appointment`, { appointment_date: date || null }, { params: formId ? { form_id: formId } : {} });
    } catch (e) {
      toast.error("Could not save appointment date.");
    }
  };

  const sendReminder = async (userId, formId) => {
    try {
      const res = await api.post(`/admin/forms/${userId}/remind`, null, { params: formId ? { form_id: formId } : {} });
      const reason = res.data?.email?.reason;
      if (reason === "email_not_configured") {
        toast.warning("Reminder logged, but email is not turned on yet. Add a Resend API key to send real emails.");
      } else {
        toast.success("Reminder email sent to the patient.");
      }
      setForms((prev) => prev.map((f) => (f.form_id === formId ? { ...f, last_reminder_at: new Date().toISOString(), reminder_count: (f.reminder_count || 0) + 1 } : f)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder.");
    }
  };

  const filtered = forms.filter(
    (f) =>
      (f.patient_name || "").toLowerCase().includes(q.toLowerCase()) ||
      (f.patient_email || "").toLowerCase().includes(q.toLowerCase()) ||
      (f.child_name || "").toLowerCase().includes(q.toLowerCase())
  );

  const submitted = forms.filter((f) => f.status === "submitted").length;
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    api.get("/billing/config").then((r) => setBanner(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {banner && !banner.onboarding_completed && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="onboarding-banner">
            <span className="text-sm font-medium text-blue-800">Finish setting up your Nimbus — add your logo, color and paperwork.</span>
            <Button size="sm" className="bg-[#0F52BA] hover:bg-[#0B3D8A]" onClick={() => navigate("/admin/onboarding")} data-testid="finish-setup-btn">Finish setup</Button>
          </div>
        )}
        {banner && banner.onboarding_completed && !banner.portal_active && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3" data-testid="portal-locked-banner">
            <span className="text-sm font-medium text-red-700">Your trial has ended and your portal is paused. Subscribe to reactivate patient intake.</span>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => navigate("/admin/settings")} data-testid="reactivate-btn">Subscribe now</Button>
          </div>
        )}
        {banner && banner.portal_active && banner.subscription_status === "trial" && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="trial-banner">
            <span className="text-sm font-medium text-amber-800">Free trial — {banner.trial_days_left ?? 0} day(s) left. Subscribe to keep your portal active.</span>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/settings")} data-testid="trial-subscribe-btn">Subscribe</Button>
          </div>
        )}
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Patient Submissions</h1>
        <p className="mt-1 text-sm text-slate-500">{user?.clinic_name ? `${user.clinic_name} — ` : ""}Review and download new-patient paperwork.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:max-w-md">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-slate-400"><Users className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Total</span></div>
            <p className="mt-1 text-2xl font-bold text-slate-900" data-testid="stat-total">{forms.length}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-slate-400"><ClipboardList className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Submitted</span></div>
            <p className="mt-1 text-2xl font-bold text-green-600" data-testid="stat-submitted">{submitted}</p>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="h-9 border-0 shadow-none focus-visible:ring-0"
              data-testid="admin-search"
            />
          </div>

          {loading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#0F52BA]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-16 text-center text-slate-400">No submissions yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient / Child</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Appointment</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((f) => (
                  <TableRow key={f.form_id || f.user_id} data-testid={`admin-row-${f.form_id || f.user_id}`}>
                    <TableCell className="font-medium text-slate-900">{f.child_name || f.patient_name || "—"}</TableCell>
                    <TableCell className="text-slate-500">
                      <div className="leading-tight">
                        <span className="block text-slate-700">{f.patient_name || "—"}</span>
                        <span className="block text-xs text-slate-400">{f.patient_email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${f.status === "submitted" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {f.status === "submitted" ? "Submitted" : "Draft"}
                      </span>
                      {f.signed_pdf_available && (
                        <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`signed-badge-${f.form_id || f.user_id}`}>
                          <FileSignature className="h-3 w-3" /> Signed ✓
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                        <input
                          type="date"
                          value={(f.appointment_date || "").slice(0, 10)}
                          onChange={(e) => setAppointment(f.user_id, f.form_id, e.target.value)}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-[#0F52BA] focus:outline-none"
                          data-testid={`appt-${f.form_id || f.user_id}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">{fmtDate(f.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {f.status !== "submitted" && (
                          <Button size="sm" variant="ghost" className="text-[#0F52BA]" onClick={() => sendReminder(f.user_id, f.form_id)} data-testid={`remind-${f.form_id || f.user_id}`} title={f.last_reminder_at ? `Reminded ${f.reminder_count || 1}x — last ${fmtDate(f.last_reminder_at)}` : "Send a reminder to finish"}>
                            <Bell className="mr-1 h-4 w-4" /> Remind{f.reminder_count ? ` (${f.reminder_count})` : ""}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/${f.user_id}${f.form_id ? `?form=${f.form_id}` : ""}`)} data-testid={`view-${f.form_id || f.user_id}`}>
                          <Eye className="mr-1 h-4 w-4" /> View
                        </Button>
                        {f.signed_pdf_available && (
                          <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" onClick={() => viewSigned(f.user_id, f.form_id)} data-testid={`signed-${f.form_id || f.user_id}`} title="View the legally-binding signed document">
                            <FileSignature className="mr-1 h-4 w-4" /> Signed
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(f.user_id, f.form_id, f.child_name || f.patient_name)} data-testid={`pdf-${f.form_id || f.user_id}`}>
                          <FileDown className="mr-1 h-4 w-4" /> PDF
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
